// TS port of pinpointedu/frontend/apps/product/src/auth/Auth0ProviderWithNavigate.jsx
// (§9 of the frontend proposal). Fails closed with a visible, honest state
// when required env vars are missing -- never a silent no-op provider.
import type { ReactNode } from 'react';
import { Auth0Provider, type AppState } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';

const AUTH0_CALLBACK_PATH = '/login';

// Dev-only mock auth (see ProtectedRoute). When it is on there is no Auth0
// session to establish, so requiring Auth0 config would block the whole app
// on credentials the mock path never uses. Gated on DEV exactly as the
// bypass itself is, so a production bundle still fails closed.
const MOCK_AUTH_ENABLED = import.meta.env.DEV && import.meta.env.VITE_MOCK_AUTH === 'true';
const LOCAL_PASSWORD_AUTH = import.meta.env.DEV && import.meta.env.VITE_LOCAL_PASSWORD_AUTH === 'true';

export function isAuth0Configured(): boolean {
  const domain = import.meta.env.VITE_AUTH0_DOMAIN?.trim();
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID?.trim();
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE?.trim();
  return Boolean(domain && clientId && audience);
}

export function Auth0ProviderWithNavigate({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  const domain = import.meta.env.VITE_AUTH0_DOMAIN?.trim();
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID?.trim();
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE?.trim();
  const redirectUri = `${window.location.origin}${AUTH0_CALLBACK_PATH}`;

  const onRedirectCallback = (appState?: AppState) => {
    // Only app-relative destinations may survive the OAuth round trip. Never
    // replay callback parameters, including an encoded /login destination.
    const returnTo = appState?.returnTo;
    let destination = '/accounts';
    if (typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//') && !returnTo.includes('\\') && !Array.from(returnTo).some((char) => char.charCodeAt(0) <= 32)) {
      try {
        const parsed = new URL(returnTo, window.location.origin);
        if (parsed.origin === window.location.origin && decodeURIComponent(parsed.pathname).replace(/\/+$/, '').toLowerCase() !== AUTH0_CALLBACK_PATH) {
          destination = `${parsed.pathname}${parsed.search}${parsed.hash}`;
        }
      } catch { /* Keep the safe signed-in destination for malformed paths. */ }
    }
    navigate(destination, { replace: true });
  };

  if (MOCK_AUTH_ENABLED || (LOCAL_PASSWORD_AUTH && !isAuth0Configured())) {
    // Render the app without an Auth0 session. The banner below is the right
    // behaviour for a real build with broken config, but here the missing
    // config is expected, not a fault.
    return <>{children}</>;
  }

  if (!(domain && clientId && audience && redirectUri)) {
    console.error('Required Auth0 configuration is missing (VITE_AUTH0_DOMAIN / VITE_AUTH0_CLIENT_ID / VITE_AUTH0_AUDIENCE).');
    return (
      <div role="alert" className="flex min-h-screen items-center justify-center p-6 text-center">
        <div>
          <h1 className="text-xl font-bold text-destructive">Authentication configuration is unavailable</h1>
          <p className="mt-2 text-muted-foreground">
            MoneySkyline could not start because Auth0 environment variables are missing. No
            demonstration session has been substituted.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience,
        scope: 'openid profile email',
      }}
      cacheLocation="memory"
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}
